import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Grid,
  VStack,
  Heading,
  useToast,
  HStack,
  Button,
} from "@chakra-ui/react";
import { SuppliersContext } from "../../Context/SuppliersContext";
import { Formik, Form } from "formik";
import { arrayToOption } from "../../../Utils/formatUtils";
import { useHistory } from "react-router-dom";
import ProductList from "./ProductList";
import CustomField from "../../Common/Form/CustomField";
import ContentCard from "../../Common/Layout/ContentCard";
import LabeledText from "../../Common/Layout/LabeledText";
import useFetch from "../../../hooks/useFetch";
import moment from "moment";
import { Prompt } from "react-router";
import ConfirmationSwal from "../../../Utils/confirmationSwal";

const PurchaseDetails = ({ purchase, updatePurchase }) => {
  const { suppliers, isLoadingSupplier } = useContext(SuppliersContext);
  const [purchaseData, setPurchaseData] = useState(purchase);
  const [purchaseStatus, setPurchaseStatus] = useState([]);
  const [isSubmitting, setIsSubmiting] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [suppliersOptions, setSuppliersOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { put, get } = useFetch();
  const history = useHistory();
  const toast = useToast();

  const setInitialData = async () => {
    setSuppliersOptions(arrayToOption(suppliers, "id", "businessName"));
    await get("/suppliers/purchases/properties/status")
      .then((res) => {
        let status = arrayToOption(res.status, "id", "status").find(
          (item) => item.value == purchaseData.statusId
        );
        setPurchaseData({ ...purchase, status });
        setPurchaseStatus(res.status);
      })
      .catch((e) => {
        console.log(e);
      });

    setIsLoading(false);
  };

  useEffect(() => {
    setInitialData();
  }, [isLoadingSupplier]);

  const submitHandler = async (value) => {
    setIsSubmiting(true);
    let data = {
      statusId: value.status.value,
      comments: value.comments,
    };
    setIsEdited(false);
    await put(`/suppliers/purchases/${purchase.id}`, data)
      .then(async (res) => {
        toast({
          title: "Compra editada con éxito.",
          description: "Se ha modificado la compra.",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
        setPurchaseData(res.purchase);
        updatePurchase(res.purchase);
      })
      .catch((e) => {
        toast({
          title: "Se ha producido un error.",
          description:
            e?.message || "Contacte con el administrador del sistema.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      })
      .finally(() => {
        setIsSubmiting(false);
      });
  };

  const handleIsEdited = () => {
    setIsEdited(true);
  };

  const handleBlockedNavigation = (nextLocation) => {
    if (isEdited) {
      setIsEdited(false);
      ConfirmationSwal.fire({
        title: "Parece que hay cambios no guardados",
        text: "Desea salir de la página?",
        icon: "question",
        confirmButtonText: "Sí, salir",
        cancelButtonText: "No",
      }).then(async (result) => {
        if (result.isConfirmed) {
          history.push(nextLocation.pathname);
        }
      });
      return false;
    }
    return true;
  };

  return (
    <>
      {isLoading ? (
        <div>Cargando</div>
      ) : (
        <Box pb="1rem">
          <ContentCard w={["auto"]}>
            <Heading variant="h2">Datos del proveedor</Heading>
            <Grid
              templateColumns={["repeat(1, 1fr)", "repeat(2, 1fr)"]}
              gap={6}
              px={["1rem", "4"]}
            >
              <Box>
                <VStack align="left" w="100%" pt="1rem">
                  <LabeledText
                    label={"Proveedor"}
                    text={purchaseData.businessName}
                  ></LabeledText>
                  <LabeledText
                    label={"CUIT"}
                    text={purchaseData.cuit}
                  ></LabeledText>
                </VStack>
              </Box>

              <Box>
                <VStack align="left" w="100%" pt="1rem">
                  <LabeledText
                    label={"Dirección"}
                    text={purchaseData.address}
                  ></LabeledText>
                  <LabeledText
                    label={"Fecha de compra"}
                    text={moment(purchaseData.purchasedAt).format("DD-MM-YYYY")}
                  ></LabeledText>
                </VStack>
              </Box>
            </Grid>
            <Formik
              initialValues={purchaseData}
              onSubmit={(values, actions) => {
                submitHandler(values);
                actions.setSubmitting(false);
              }}
            >
              {(props) => (
                <Form>
                  <Grid
                    px={["1rem", "4"]}
                    my={"2rem"}
                    templateColumns={["repeat(1, 1fr)", "repeat(2, 1fr)"]}
                    gap={6}
                  >
                    <CustomField
                      name="comments"
                      title="Observaciones: "
                      inputType="textArea"
                      props={props}
                      customStyle={{ textTransform: "uppercase" }}
                      setIsEdited={handleIsEdited}
                    ></CustomField>
                    <CustomField
                      name="status"
                      title="Estado"
                      placeholder="Elija un estado"
                      inputType="select"
                      options={arrayToOption(purchaseStatus, "id", "status")}
                      props={props}
                      isClearable={false}
                      customStyle={{ textTransform: "uppercase" }}
                      setIsEdited={handleIsEdited}
                    ></CustomField>
                  </Grid>

                  <HStack
                    align="center"
                    justify="flex-end"
                    spacing="5"
                    paddingTop="3rem"
                  >
                    <Button variant="ghost" onClick={() => history.goBack()}>
                      Cancelar
                    </Button>
                    <Button
                      colorScheme="blue"
                      isLoading={isSubmitting}
                      type="submit"
                    >
                      Guardar
                    </Button>
                  </HStack>
                </Form>
              )}
            </Formik>
          </ContentCard>

          <ContentCard w={["auto"]}>
            <ProductList purchase={purchase} setIsEdited={handleIsEdited} />
          </ContentCard>
        </Box>
      )}
      <Prompt when={isEdited} message={(e) => handleBlockedNavigation(e)} />
    </>
  );
};

export default PurchaseDetails;
